import posthog from 'posthog-js'
import React, {useState, useEffect, useRef, useCallback} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {AnimatePresence, motion} from 'framer-motion'
import useIsMounted from '../../hooks/useIsMounted'
import {Block, Col, Row} from 'jsxstyle'
import DynamicFormField from '../../components/FormField'
import {ReactComponent as CloseIcon} from '../../common/assets/close-icon.svg'
import {ReactComponent as AddIcon} from '../../common/assets/add-fill-icon.svg'
import Loader from '../../components/Loader'
import {useXpertKnowledgeContext} from '../XpertKnowledge/XpertKnowledgeContext'
import {useXpertKnowledgeTutorialContext} from './XpertKnowledgeTutorialContext'
import XpertKnowledgeTutorialEditSection from './components/Edit/XpertKnowledgeTutorialEditSection'
import {XpertKnowledgeSectionDto} from '../../services/XpertKnowledgeSection/type'
import {XpertKnowledgeTutorialDto, XpertKnowledgeTutorialStatus} from '../../services/XpertKnowledgeTutorial/type'
import ActivityLogsDialog from '../../components/ActivityLogsDialog'
import {useXpertKnowledgeAuthContext} from '../../components/AuthContext'
import {UserRole} from '../../services/User/type'
import i18n from '../../common/i18n'
import QuickConfirmDialog from '../../components/QuickConfirmDialog'

const animationVariants = {
  initial: {x: '100%', opacity: 0},
  enter: {x: '0%', opacity: 1},
  exit: {x: '100%', opacity: 0},
}

const XpertKnowledgeTutorialEdit: React.FC = () => {
  const {xpertKnowledgeId} = useParams<{xpertKnowledgeId: string}>()
  const {xpertKnowledgesPublish, xpertKnowledgesUnpublish, setSelectedXpertKnowledge, selectedXpertKnowledge, deleteXpertKnowledge} =
    useXpertKnowledgeContext()
  const {user} = useXpertKnowledgeAuthContext()
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)
  const {
    xpertKnowledgeSections,
    selectedXpertKnowledgeTutorial,
    updateXpertKnowledgeTutorial,
    submitXpertKnowledgeTutorial,
    createXpertKnowledgeTutorialSection,
    createXpertKnowledgeTutorialSubSection,
    updateXpertKnowledgeTutorialSection,
    updateXpertKnowledgeTutorialSubSection,
  } = useXpertKnowledgeTutorialContext()
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false)
  const [openDeleteXKConfirm, setOpenDeleteXKConfirm] = useState(false)

  const isMounted = useIsMounted()
  const popupRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const [editedXpertKnowledgeTutorial, setEditedXpertKnowledgeTutorial] = useState<XpertKnowledgeTutorialDto | undefined>(
    selectedXpertKnowledgeTutorial
  )
  const [editedTutorial, setEditedTutorial] = useState<XpertKnowledgeSectionDto[]>(xpertKnowledgeSections)

  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (xpertKnowledgeId) {
      setSelectedXpertKnowledge(
        xpertKnowledgesPublish.find((xpertKnowledge) => xpertKnowledge.id === xpertKnowledgeId) ||
          xpertKnowledgesUnpublish.find((xpertKnowledge) => xpertKnowledge.id === xpertKnowledgeId) ||
          undefined
      )
    }
  }, [xpertKnowledgeId, xpertKnowledgesPublish, xpertKnowledgesUnpublish])

  useEffect(() => {
    setEditedTutorial(xpertKnowledgeSections)
  }, [xpertKnowledgeSections])

  const isActionAllowed = useCallback(() => {
    const hasDraftOrRefusedStatus =
      selectedXpertKnowledgeTutorial?.status === XpertKnowledgeTutorialStatus.DRAFT ||
      selectedXpertKnowledgeTutorial?.status === XpertKnowledgeTutorialStatus.REFUSED

    return (hasDraftOrRefusedStatus && user?.role === UserRole.AGENT) || user?.role !== UserRole.AGENT
  }, [selectedXpertKnowledgeTutorial?.status, user?.role])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node) && !openDeleteConfirm) {
        event.stopPropagation()
        handleClose()
      }
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !openDeleteConfirm) {
        event.stopPropagation()
        handleClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [openDeleteConfirm])

  const handleClose = useCallback(() => {
    if (isMounted()) navigate('..')
  }, [isMounted])

  const [savingAction, setSavingAction] = useState<'save' | 'publish' | null>(null)

  const handleSaveWithAction = async (action: 'save' | 'publish', comment?: string) => {
    setSavingAction(action)
    setIsSaving(true)
    try {
      if (action === 'publish') {
        await submitXpertKnowledgeTutorial({
          comment: comment || '',
          title: editedXpertKnowledgeTutorial?.title,
          description: editedXpertKnowledgeTutorial?.description,
        })
      } else if (editedXpertKnowledgeTutorial) {
        await updateXpertKnowledgeTutorial({
          title: editedXpertKnowledgeTutorial.title,
          description: editedXpertKnowledgeTutorial.description,
        })
      }

      for (const section of editedTutorial) {
        if ((section as any).isNew) {
          const createdSection = await createXpertKnowledgeTutorialSection(section.title, section.position, section.intro, section.conclusion)
          if (section.subSections) {
            for (const subSection of section.subSections) {
              if ((subSection as any).isNew) {
                await createXpertKnowledgeTutorialSubSection(createdSection!.id, subSection.title, subSection.position, subSection.content)
              }
            }
          }
        } else {
          await updateXpertKnowledgeTutorialSection(section.id, section.title, section.intro, section.conclusion)
          section.subSections?.forEach(async (subSection) => {
            if ((subSection as any).isNew) {
              await createXpertKnowledgeTutorialSubSection(section.id, subSection.title, subSection.position, subSection.content)
            } else {
              await updateXpertKnowledgeTutorialSubSection(
                section.id,
                subSection.id,
                subSection.title,
                subSection.medias,
                subSection.content,
                subSection.position
              )
            }
          })
        }
      }

      handleClose()
    } catch (error) {
      console.error(error)
    } finally {
      setIsSaving(false)
      setSavingAction(null)
    }
  }

  if (!selectedXpertKnowledge) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{opacity: 0}}
        animate={{opacity: 1}}
        exit={{opacity: 0}}
        transition={{duration: 0.3}}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          zIndex: 1,
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            handleClose()
          }
        }}
      />
      <motion.div
        ref={popupRef}
        initial="initial"
        animate="enter"
        exit="exit"
        variants={animationVariants}
        transition={{duration: 0.3}}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '58%',
          height: '100%',
          backgroundColor: '#f7f8f9',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          overflowY: 'hidden',
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Block flex={1} overflowY="auto" padding="20px 30px" paddingBottom="100px">
          <Col gap="20px" width="100%">
            <Row justifyContent="space-between">
              <Row>
                <Block fontSize="18px" lineHeight="24px" fontWeight={500}>
                  {i18n.t('xk.tutorial.edit.header')}
                </Block>
              </Row>
              <CloseIcon
                width={20}
                height={20}
                color="#B2D0FF"
                style={{
                  cursor: 'pointer',
                }}
                onClick={() => handleClose()}
              />
            </Row>
            {selectedXpertKnowledge.publishTutorial?.id !== selectedXpertKnowledgeTutorial?.id && (
              <Block width="95%" backgroundColor="#FFF0E3" borderRadius="10px" padding="12px 20px" fontSize="14px" fontWeight={400}>
                {i18n.t('xk.tutorial.edit.versionNotice', {version: selectedXpertKnowledgeTutorial?.version})}
              </Block>
            )}
            {selectedXpertKnowledge.publishTutorial?.status === XpertKnowledgeTutorialStatus.IN_REVIEW && user?.role == UserRole.AGENT && (
              <Block width="95%" backgroundColor="#FFF0E3" color="#AD5400" borderRadius="10px" padding="12px 20px" fontSize="14px" fontWeight={400}>
                {i18n.t('xk.tutorial.edit.inReviewNotice')}
              </Block>
            )}
            {selectedXpertKnowledge.publishTutorial?.status === XpertKnowledgeTutorialStatus.DRAFT && (
              <Block width="95%" backgroundColor="#FFF0E3" color="#AD5400" borderRadius="10px" padding="12px 20px" fontSize="14px" fontWeight={400}>
                {i18n.t('xk.tutorial.edit.inDraftNotice')}
              </Block>
            )}
            {selectedXpertKnowledge.publishTutorial?.status === XpertKnowledgeTutorialStatus.REFUSED && (
              <Block width="95%" backgroundColor="#FFF0E3" color="#AD5400" borderRadius="10px" padding="12px 20px" fontSize="14px" fontWeight={400}>
                {i18n.t('xk.tutorial.edit.inRefusedNotice')}
              </Block>
            )}
            <Col gap="8px">
              <Block fontSize="14px" lineHeight="24px" fontWeight={500}>
                {i18n.t('xk.tutorial.edit.fields.title')}
              </Block>
              <DynamicFormField
                placeholder={i18n.t('xk.tutorial.edit.fields.titlePlaceholder')}
                value={selectedXpertKnowledgeTutorial?.title || ''}
                onEdit={(value) => setEditedXpertKnowledgeTutorial((prev) => ({...prev!, title: value}))}
              />
            </Col>
            <Col gap="8px">
              <Block fontSize="14px" lineHeight="24px" fontWeight={500}>
                {i18n.t('xk.tutorial.edit.fields.description')}
              </Block>
              <DynamicFormField
                placeholder={i18n.t('xk.tutorial.edit.fields.descriptionPlaceholder')}
                value={selectedXpertKnowledgeTutorial?.description || ''}
                onEdit={(value) => setEditedXpertKnowledgeTutorial((prev) => ({...prev!, description: value}))}
              />
            </Col>
            {editedTutorial.map((section, index) => (
              <XpertKnowledgeTutorialEditSection
                key={section.id}
                xpertKnowledgeSection={section}
                index={index}
                onSectionChange={(updatedSection) =>
                  setEditedTutorial((prev) => prev.map((sec) => (sec.id === updatedSection.id ? updatedSection : sec)))
                }
                onDeleteSection={(sectionId: number) => setEditedTutorial((prev) => prev.filter((section) => section.id !== sectionId))}
                onDeleteSubSection={(subSectionId: number) =>
                  setEditedTutorial((prev) =>
                    prev.map((section) => ({
                      ...section,
                      subSections: section.subSections?.filter((subSection) => subSection.id !== subSectionId),
                    }))
                  )
                }
                onOpenDeleteConfirm={setOpenDeleteConfirm}
              />
            ))}
            <Row
              component="button"
              cursor="pointer"
              justifyContent="center"
              alignItems="center"
              borderRadius="12px"
              border="1px solid #0E78F5"
              backgroundColor="transparent"
              hoverBackgroundColor="rgba(14, 120, 245, 0.1)"
              hoverColor="#0E78F5"
              transition="background-color 0.3s ease, transform 0.2s ease"
              padding="12px 24px"
              width="100%"
              props={{
                onClick: () => {
                  const maxPosition = editedTutorial.reduce((max, section) => (section.position > max ? section.position : max), 0)
                  const newSection: XpertKnowledgeSectionDto & {isNew?: boolean} = {
                    id: Date.now(),
                    title: 'New Section',
                    intro: '',
                    conclusion: '',
                    subSections: [],
                    position: maxPosition + 1,
                    isNew: true,
                  }
                  setEditedTutorial((prev) => [...prev, newSection])
                },
              }}
            >
              <AddIcon width="16" height="16" color="#0E78F5" style={{marginRight: '8px'}} />
              <Block fontSize="14px" lineHeight="24px" fontWeight={500} color="#0E78F5" marginTop="1px">
                {i18n.t('xk.tutorial.edit.sections.addSection')}
              </Block>
            </Row>
          </Col>
        </Block>

        <Block
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          backgroundColor="#FFFFFF"
          padding="20px 30px"
          boxShadow="0px -4px 8px rgba(0, 0, 0, 0.05)"
          borderTop="1px solid rgba(98, 98, 98, 0.3)"
          zIndex={3}
        >
          <Row width="100%" gap="20px" justifyContent="center">
            <Block
              component="button"
              fontSize="14px"
              lineHeight="24px"
              fontWeight={400}
              cursor={isSaving ? 'not-allowed' : 'pointer'}
              border="1px solid #0E78F5"
              borderRadius="4px"
              backgroundColor="transparent"
              color="#0E78F5"
              hoverBackgroundColor="rgba(14, 120, 245, 0.1)"
              transition="background-color 0.3s ease, transform 0.2s ease"
              padding="4px 8px"
              width="40%"
              props={{
                onClick: handleClose,
                disabled: isSaving,
              }}
            >
              {i18n.t('xk.tutorial.edit.actions.cancel')}
            </Block>
            <Block
              component="button"
              fontSize="14px"
              lineHeight="24px"
              fontWeight={400}
              cursor={!isActionAllowed() || isSaving ? 'not-allowed' : 'pointer'}
              borderRadius="4px"
              border={!isActionAllowed() ? 'none' : '1px solid #AB0D0D'}
              backgroundColor={!isActionAllowed() ? '#DADCE0' : '#FFFFFF'}
              color={!isActionAllowed() ? '#FFFFFF' : '#AB0D0D'}
              hoverBackgroundColor={!isActionAllowed() ? '#DADCE0' : '#FDE8E8'}
              transition="background-color 0.3s ease, transform 0.2s ease"
              padding="4px 8px"
              width="40%"
              props={{
                onClick: () => {
                  setOpenDeleteXKConfirm(true)
                },
                disabled: !isActionAllowed() || isSaving,
              }}
            >
              {i18n.t('xk.tutorial.edit.actions.delete.button')}
            </Block>
            <Block
              component="button"
              fontSize="14px"
              lineHeight="24px"
              fontWeight={400}
              cursor={!isActionAllowed() || isSaving ? 'not-allowed' : 'pointer'}
              borderRadius="4px"
              border={!isActionAllowed() ? 'none' : '1px solid #0E78F5'}
              backgroundColor={!isActionAllowed() ? '#DADCE0' : '#0E78F5'}
              color="#ffffff"
              hoverBackgroundColor={!isActionAllowed() ? '#DADCE0' : isSaving ? '#0E78F5' : '#0A5EC2'}
              transition="background-color 0.3s ease, transform 0.2s ease"
              padding="4px 8px"
              width="40%"
              props={{
                onClick: () => {
                  if (isActionAllowed()) {
                    handleSaveWithAction('save')
                    posthog.capture('tutorial_edit_saved')
                  }
                },
                disabled: !isActionAllowed() || isSaving,
              }}
            >
              {savingAction === 'save' && isSaving ? <Loader size={20} /> : i18n.t('xk.tutorial.edit.actions.save')}
            </Block>
            {user?.role === UserRole.AGENT && (
              <Block
                component="button"
                fontSize="14px"
                lineHeight="24px"
                fontWeight={400}
                cursor={!isActionAllowed() || isSaving ? 'not-allowed' : 'pointer'}
                borderRadius="4px"
                border={!isActionAllowed() ? 'none' : 'none'}
                backgroundColor={!isActionAllowed() ? '#DADCE0' : '#0A5EC2'}
                color="#ffffff"
                padding="12px 8px"
                width="40%"
                hoverBackgroundColor={!isActionAllowed() ? '#DADCE0' : '#0E78F5'}
                boxShadow="0px 4px 6px rgba(0, 0, 0, 0.1)"
                transition="background 0.3s ease, transform 0.2s ease"
                props={{
                  onClick: () => {
                    if (isActionAllowed()) {
                      setIsConfirmDialogOpen(true)
                    }
                  },
                  disabled: !isActionAllowed() || isSaving,
                }}
              >
                {savingAction === 'publish' && isSaving ? <Loader size={20} /> : i18n.t('xk.tutorial.edit.actions.saveAskApproval')}
              </Block>
            )}
          </Row>
        </Block>
        <QuickConfirmDialog
          key={selectedXpertKnowledge.id}
          open={openDeleteXKConfirm}
          setOpen={() => {
            setOpenDeleteXKConfirm(false)
          }}
          title={i18n.t('xk.tutorial.edit.actions.delete.title')}
          content={i18n.t('xk.tutorial.edit.actions.delete.subTitle')}
          buttons={[
            {
              title: i18n.t('xk.tutorial.edit.actions.delete.button'),
              onClick: () => {
                deleteXpertKnowledge(selectedXpertKnowledge.id)
                navigate('/xpert-knowledge')
              },
              closeOnClick: true,
              customButtonProps: {
                backgroundColor: '#F1164A',
                hoverBackgroundColor: '#C41E2C',
                color: '#FFFFFF',
              },
            },
          ]}
        />
        <ActivityLogsDialog
          title={i18n.t('xk.tutorial.edit.dialogs.approvalRequest.title')}
          subtitle={i18n.t('xk.tutorial.edit.dialogs.approvalRequest.subtitle')}
          sendLabel={i18n.t('xk.tutorial.edit.dialogs.approvalRequest.sendLabel')}
          onSend={(value) => {
            setIsConfirmDialogOpen(false)
            handleSaveWithAction('publish', value)
          }}
          placeholder={i18n.t('xk.tutorial.edit.dialogs.approvalRequest.placeholder')}
          onCancel={() => setIsConfirmDialogOpen(false)}
          open={isConfirmDialogOpen}
          setOpen={setIsConfirmDialogOpen}
        />
      </motion.div>
    </AnimatePresence>
  )
}

export default XpertKnowledgeTutorialEdit
